import { MESSAGE_CHANNELS_V3 } from "@redotech/redo-flows-common/rules-v3/types";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { AI_NO_REPLY_TAG_NAME } from "@redotech/redo-support-tags-common/system-tag-names";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { shopperAgentOutreachLaneCondition } from "../shopper-agent-outreach-lane";
import { notSpamCondition } from "../spam-guard";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob. Derived from the merchant's
 * Shopper Agent settings, never hand-edited: `sms` is always in, and the email
 * transports (`SHOPPER_AGENT_EMAIL_REPLY_CHANNELS`) join only once the merchant
 * turns email replies on.
 */
export const SendAiResponseOnShopperAgentConversationConfigSchema =
  Schema.Struct({
    channels: Schema.Array(Schema.Literal(...MESSAGE_CHANNELS_V3)),
  });

export type SendAiResponseOnShopperAgentConversationConfig =
  typeof SendAiResponseOnShopperAgentConversationConfigSchema.Type;

const bind = makeBind<SendAiResponseOnShopperAgentConversationConfig>();

/**
 * Default rule: Send AI response on a Shopper Agent conversation
 *
 * Shopper Agent owns its own conversations, so they reply on the agent's gate
 * rather than the merchant's Support AI channel list — a merchant who never
 * enabled AI autopilot for SMS still gets agent replies on agent threads.
 *
 * Every gate is a condition on one flat step, so the rule prunes in-process
 * (`evaluateRulePrefilter` reads exactly this step) and can only ever reply:
 *   - the ticket is on a channel in `configurations.channels`. `sms` is always
 *     there; the email transports are added only when the merchant turns
 *     Shopper Agent email replies on. Anything outside the list resolves to
 *     that channel's ordinary support agent, which is the merchant's Support
 *     AI config to enable, not this rule's. The one exception is an AI-owned
 *     email lane while replies are off: `default-unassign-ai-on-shopper-agent-
 *     email-conversation` releases it so it is not left with a silent assignee
 *   - the thread is the live continuation lane of a `shopper_agent_conversation`
 *   - AI owns the ticket, so the agent never answers over a human
 *   - the ticket is not tagged SPAM or AI_NO_REPLY
 *
 * The channel gate leads because it is the only one answerable from the
 * in-memory context: an AND group short-circuits in order, so a message on an
 * unlisted channel never pays for the lane lookup that would otherwise run on
 * every inbound message. It also prefilters (`TICKET_CHANNEL` is prunable, the
 * lane is not), so traffic this rule cannot answer never starts a workflow.
 *
 * Always enabled and hidden. `configurations` is derived from the team's
 * Shopper Agent settings rather than edited here, so there is no merchant-
 * facing form. When the team turns Shopper Agent off, the lane goes false.
 *
 * Owns agent lanes outright. `sortIndex: 1` runs it ahead of every other
 * `CONVERSATION_MESSAGE_CREATED` rule (its email release sibling shares the
 * slot; the two never both fire), and
 * `default-send-ai-response-on-customer-reply` carries the mirror-image
 * `SHOPPER_AGENT_OUTREACH IS_NOT` guard on both of its branches, so on an
 * agent lane that rule neither replies nor unassigns the AI out from under this
 * one. The two can no longer both reach `START_AI_AGENT_EXECUTION`.
 */
export const sendAiResponseOnShopperAgentConversationRule = {
  id: "default-send-ai-response-on-shopper-agent-conversation",
  name: "Send AI response on a Shopper Agent conversation",
  description:
    "When a customer replies on a Shopper Agent conversation the AI owns, and the ticket isn't tagged SPAM or AI_NO_REPLY, have the agent respond.",
  hidden: true,
  triggerType: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,

  configSchema: SendAiResponseOnShopperAgentConversationConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-send-ai-response-on-shopper-agent-conversation",
    enabled: true,
    // Execution order only: hidden rules are excluded from
    // `maxVisibleDefaultRuleSortIndex`, so the merchant's list is unchanged.
    sortIndex: 1,
    configurations: { channels: ["sms"] },
  },

  template: {
    name: "Send AI response on a Shopper Agent conversation",
    description:
      "When a customer replies on a Shopper Agent conversation the AI owns, and the ticket isn't tagged SPAM or AI_NO_REPLY, have the agent respond.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
        nextId: "shopper-agent-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "shopper-agent-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              // RCS and iMessage normalize to `sms`
              // (`integrationTypeToChannel`).
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: bind("channels", ["sms"]),
              queryOperator: QueryOperator.IS,
            },
            shopperAgentOutreachLaneCondition(QueryOperator.IS),
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            notSpamCondition(),
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [AI_NO_REPLY_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [{ actionType: RuleActionType.START_AI_AGENT_EXECUTION }],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<
  string,
  SendAiResponseOnShopperAgentConversationConfig
>;
